/**
 * Module: `export/markdown-to-pdfmake.ts` — pragmatic markdown → pdfmake content.
 * Supports headings, paragraphs, emphasis, links, lists, tables, fenced code.
 * Unsupported constructs degrade to plain text.
 */

import type {
  Heading,
  List,
  ListItem,
  Nodes,
  Paragraph,
  PhrasingContent,
  Root,
  RootContent,
  Table,
  Text,
} from "mdast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";

import {
  EXPORT_COLOR_INLINE_CODE,
  EXPORT_COLOR_LINK,
  EXPORT_COLOR_SECONDARY,
  EXPORT_COLOR_SEPARATOR,
  EXPORT_H1_AFTER_PT,
  EXPORT_H2_AFTER_PT,
  EXPORT_H2_BEFORE_PT,
  EXPORT_H3_AFTER_PT,
  EXPORT_H3_BEFORE_PT,
  EXPORT_INLINE_CODE_FONT_SIZE_PT,
  EXPORT_LIST_AFTER_PT,
  EXPORT_PARAGRAPH_AFTER_PT,
  EXPORT_SEPARATOR_LINE_WIDTH_PT,
  EXPORT_SEPARATOR_MARGIN_PT,
  EXPORT_SEPARATOR_WIDTH_PT,
  exportColorCss,
} from "@/lib/export/export-styles";
import { htmlTablesToGfm } from "@/lib/export/html-tables-to-gfm";
import { splitPdfTextWithEmoji } from "@/lib/export/pdf-emoji";
import { RESPONSE_EXPORT_DISCLAIMER } from "@/lib/export/response-export-document";

const LINK_COLOR_CSS = exportColorCss(EXPORT_COLOR_LINK);
const INLINE_CODE_COLOR_CSS = exportColorCss(EXPORT_COLOR_INLINE_CODE);
const SECONDARY_COLOR_CSS = exportColorCss(EXPORT_COLOR_SECONDARY);

type PdfTextSpan =
  | string
  | {
      text: string | PdfTextSpan[];
      font?: string;
      bold?: boolean;
      italics?: boolean;
      link?: string;
      decoration?: "underline";
      fontSize?: number;
      color?: string;
      style?: string;
    };

export type PdfMakeContentNode = Record<string, unknown>;

const BARE_URL_REGEX = /https?:\/\/[^\s<>[\]()]+/g;

function pushPlainWithEmoji(spans: PdfTextSpan[], value: string): void {
  spans.push(...(splitPdfTextWithEmoji(value) as PdfTextSpan[]));
}

function phrasingPlainText(spans: PdfTextSpan[]): string {
  return spans
    .map((span) => {
      if (typeof span === "string") return span;
      if (typeof span.text === "string") return span.text;
      if (Array.isArray(span.text)) return phrasingPlainText(span.text);
      return "";
    })
    .join("");
}

function linkifyPlainText(value: string): PdfTextSpan[] {
  const spans: PdfTextSpan[] = [];
  let lastIndex = 0;
  for (const match of value.matchAll(BARE_URL_REGEX)) {
    const url = match[0] ?? "";
    const index = match.index ?? 0;
    if (index > lastIndex) {
      pushPlainWithEmoji(spans, value.slice(lastIndex, index));
    }
    // Trim common trailing punctuation from autolinked URLs.
    const cleaned = url.replace(/[.,);:]+$/u, "");
    const trailing = url.slice(cleaned.length);
    spans.push({
      text: cleaned,
      link: cleaned,
      style: "link",
      color: LINK_COLOR_CSS,
      decoration: "underline",
    });
    if (trailing) pushPlainWithEmoji(spans, trailing);
    lastIndex = index + url.length;
  }
  if (lastIndex < value.length) {
    pushPlainWithEmoji(spans, value.slice(lastIndex));
  }
  return spans.length > 0 ? spans : splitPdfTextWithEmoji(value);
}

function applyBoldToSpans(spans: PdfTextSpan[]): PdfTextSpan[] {
  return spans.map((span) => {
    if (typeof span === "string") return { text: span, bold: true };
    return {
      ...span,
      bold: true,
      text: Array.isArray(span.text) ? applyBoldToSpans(span.text) : span.text,
    };
  });
}

function applyUnderlineToSpans(spans: PdfTextSpan[]): PdfTextSpan[] {
  return spans.map((span) => {
    if (typeof span === "string") {
      return { text: span, decoration: "underline" as const };
    }
    return {
      ...span,
      decoration: "underline" as const,
      text: Array.isArray(span.text)
        ? applyUnderlineToSpans(span.text)
        : span.text,
    };
  });
}

function applyColorToSpans(spans: PdfTextSpan[], color: string): PdfTextSpan[] {
  return spans.map((span) => {
    if (typeof span === "string") return { text: span, color };
    return {
      ...span,
      color,
      text: Array.isArray(span.text)
        ? applyColorToSpans(span.text, color)
        : span.text,
    };
  });
}

/**
 * Apply the URL to every leaf span. pdfmake 0.3 does not propagate `link`
 * from an inline object whose `text` is another inline array, so retaining
 * that nested shape renders link styling but no clickable PDF annotation.
 */
function htmlPhrasingToPdf(html: string): PdfTextSpan[] {
  if (!/<[a-z][\s\S]*>/i.test(html)) {
    return linkifyPlainText(html);
  }
  const doc = new DOMParser().parseFromString(
    `<div>${html}</div>`,
    "text/html"
  );
  const root = doc.body.firstElementChild;
  if (!root) return linkifyPlainText(html);
  return htmlDomNodesToPdf(root.childNodes);
}

function htmlDomNodesToPdf(nodes: NodeListOf<ChildNode>): PdfTextSpan[] {
  const out: PdfTextSpan[] = [];
  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(...linkifyPlainText(node.textContent ?? ""));
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === "br") {
      out.push("\n");
      continue;
    }
    const inner = htmlDomNodesToPdf(el.childNodes);
    if (tag === "a") {
      const href = (el.getAttribute("href") ?? "").trim();
      const fallback = [(el.textContent ?? "").replace(/\s+/g, " ").trim()];
      out.push(
        ...(href
          ? applyLinkToSpans(inner.length > 0 ? inner : fallback, href)
          : inner)
      );
      continue;
    }
    out.push(...inner);
  }
  return out;
}

/**
 * Apply the URL to every leaf span. pdfmake 0.3 does not propagate `link`
 * from an inline object whose `text` is another inline array, so retaining
 * that nested shape renders link styling but no clickable PDF annotation.
 */
function applyLinkToSpans(spans: PdfTextSpan[], url: string): PdfTextSpan[] {
  return spans.map((span) => {
    if (typeof span === "string") {
      return {
        text: span,
        link: url,
        style: "link",
        color: LINK_COLOR_CSS,
        decoration: "underline" as const,
      };
    }
    return {
      ...span,
      link: url,
      style: "link",
      color: LINK_COLOR_CSS,
      decoration: "underline" as const,
      text: Array.isArray(span.text)
        ? applyLinkToSpans(span.text, url)
        : span.text,
    };
  });
}

function phrasingToPdf(nodes: PhrasingContent[]): PdfTextSpan[] {
  const out: PdfTextSpan[] = [];

  for (const node of nodes) {
    switch (node.type) {
      case "text":
        out.push(...linkifyPlainText(node.value));
        break;
      case "strong": {
        const inner = phrasingToPdf(node.children);
        const plain = phrasingPlainText(inner).trim();
        // Field labels (`Résumé :`, `URL :`, …): bold + underline.
        const isFieldLabel = /:\s*$/.test(plain);
        // Titles like `**[Doc](url)**`: promote bold onto the link span.
        const withBold = applyBoldToSpans(inner);
        if (plain === RESPONSE_EXPORT_DISCLAIMER) {
          out.push(...applyColorToSpans(withBold, SECONDARY_COLOR_CSS));
        } else if (isFieldLabel) {
          out.push(...applyUnderlineToSpans(withBold));
        } else {
          out.push(...withBold);
        }
        break;
      }
      case "emphasis":
        out.push({ text: phrasingToPdf(node.children), italics: true });
        break;
      case "inlineCode":
        out.push({
          text: node.value,
          fontSize: EXPORT_INLINE_CODE_FONT_SIZE_PT,
          color: INLINE_CODE_COLOR_CSS,
        });
        break;
      case "link": {
        const inner = phrasingToPdf(node.children);
        const onlyStrong =
          node.children.length === 1 && node.children[0]?.type === "strong";
        const linked = applyLinkToSpans(inner, node.url);
        out.push(...(onlyStrong ? applyBoldToSpans(linked) : linked));
        break;
      }
      case "html": {
        out.push(...htmlPhrasingToPdf(node.value));
        break;
      }
      case "break":
        out.push("\n");
        break;
      default:
        if ("children" in node && Array.isArray(node.children)) {
          out.push(...phrasingToPdf(node.children as PhrasingContent[]));
        } else if ("value" in node && typeof node.value === "string") {
          out.push(...linkifyPlainText(node.value));
        }
        break;
    }
  }

  return out;
}

function paragraphToPdf(node: Paragraph): PdfMakeContentNode {
  return {
    text: phrasingToPdf(node.children),
    margin: [0, 0, 0, EXPORT_PARAGRAPH_AFTER_PT],
  };
}

function headingToPdf(node: Heading): PdfMakeContentNode {
  const style = node.depth <= 1 ? "h1" : node.depth === 2 ? "h2" : "h3";
  const margin =
    node.depth <= 1
      ? ([0, 0, 0, EXPORT_H1_AFTER_PT] as const)
      : node.depth === 2
        ? ([0, EXPORT_H2_BEFORE_PT, 0, EXPORT_H2_AFTER_PT] as const)
        : ([0, EXPORT_H3_BEFORE_PT, 0, EXPORT_H3_AFTER_PT] as const);
  return {
    text: phrasingToPdf(node.children),
    style,
    // Keep the document title from colliding with the page top edge.
    margin: [...margin],
  };
}

function listItemChildren(item: ListItem): PdfMakeContentNode[] {
  return item.children.flatMap((child) => blockToPdf(child));
}

function listToPdf(node: List): PdfMakeContentNode {
  const items = node.children.map((item) => {
    const parts = listItemChildren(item);
    if (parts.length === 1) return parts[0]!;
    return { stack: parts };
  });
  if (node.ordered) {
    return { ol: items, margin: [0, 0, 0, EXPORT_LIST_AFTER_PT] };
  }
  return { ul: items, margin: [0, 0, 0, EXPORT_LIST_AFTER_PT] };
}

function tableCellText(
  cell: Table["children"][number]["children"][number]
): PdfTextSpan[] {
  const spans = phrasingToPdf(cell.children as PhrasingContent[]);
  return spans.length > 0 ? spans : [""];
}

function tableCellToPdf(
  cell: Table["children"][number]["children"][number]
): PdfMakeContentNode {
  return {
    text: tableCellText(cell),
    verticalAlignment: "middle",
  };
}

function tableToPdf(node: Table): PdfMakeContentNode {
  const body = node.children.map((row) =>
    row.children.map((cell) => tableCellToPdf(cell))
  );
  const colCount = Math.max(...body.map((row) => row.length), 1);
  const emptyCell: PdfMakeContentNode = {
    text: "",
    verticalAlignment: "middle",
  };
  return {
    table: {
      headerRows: 1,
      widths: Array.from({ length: colCount }, () => "*"),
      body: body.map((row) => {
        const padded = [...row];
        while (padded.length < colCount) padded.push(emptyCell);
        return padded;
      }),
    },
    layout: "lightHorizontalLines",
    margin: [0, 4, 0, 10],
  };
}

function codeToPdf(node: Text): PdfMakeContentNode {
  return {
    text: node.value.replace(/\t/g, "  "),
    style: "code",
    margin: [0, 4, 0, 10],
    preserveLeadingSpaces: true,
  };
}

function extractPlainText(node: Nodes): string {
  if ("value" in node && typeof node.value === "string") return node.value;
  if ("children" in node && Array.isArray(node.children)) {
    return (node.children as Nodes[])
      .map((child) => extractPlainText(child))
      .join("");
  }
  return "";
}

function plainFallback(node: RootContent): PdfMakeContentNode {
  const text = extractPlainText(node).trim();
  return {
    text: text || "",
    margin: text ? [0, 0, 0, EXPORT_PARAGRAPH_AFTER_PT] : [0, 0, 0, 0],
  };
}

function blockToPdf(node: RootContent): PdfMakeContentNode[] {
  switch (node.type) {
    case "paragraph":
      return [paragraphToPdf(node)];
    case "heading":
      return [headingToPdf(node)];
    case "list":
      return [listToPdf(node)];
    case "table":
      return [tableToPdf(node)];
    case "code":
      return [codeToPdf(node)];
    case "thematicBreak":
      return [
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: EXPORT_SEPARATOR_WIDTH_PT,
              y2: 0,
              lineWidth: EXPORT_SEPARATOR_LINE_WIDTH_PT,
              lineColor: exportColorCss(EXPORT_COLOR_SEPARATOR),
            },
          ],
          margin: [
            0,
            EXPORT_SEPARATOR_MARGIN_PT,
            0,
            EXPORT_SEPARATOR_MARGIN_PT,
          ],
        },
      ];
    case "blockquote":
      return node.children.flatMap((child) => blockToPdf(child));
    default:
      return [plainFallback(node)];
  }
}

export function markdownToPdfMakeContent(
  markdown: string
): PdfMakeContentNode[] {
  const tree = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .parse(htmlTablesToGfm(markdown)) as Root;
  return tree.children.flatMap((child) => blockToPdf(child));
}
